import { snap, wrapAngle, type Vec3 } from '../../utils/math';

/**
 * Rigid transform on the track: position + heading (yaw around +Y).
 * Track pieces only ever rotate around the up axis, which keeps connector
 * math exact and cheap. yaw = 0 faces +Z, yaw = +π/2 faces +X (a right turn).
 * Matches Babylon's left-handed `rotation.y`.
 */
export interface Pose {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
}

export const IDENTITY_POSE: Pose = Object.freeze({ x: 0, y: 0, z: 0, yaw: 0 });

export function pose(x: number, y: number, z: number, yaw = 0): Pose {
  return { x, y, z, yaw };
}

// Quarter turns are the common case: snapping cos/sin keeps long chains of
// segments free of accumulated floating point drift.
function cosSin(yaw: number): [number, number] {
  return [snap(Math.cos(yaw), 1e-12), snap(Math.sin(yaw), 1e-12)];
}

/** Transforms a point from pose-local space into the pose's parent space. */
export function transformPoint(p: Pose, local: Readonly<Vec3>, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  const [c, s] = cosSin(p.yaw);
  const x = p.x + local.x * c + local.z * s;
  const z = p.z - local.x * s + local.z * c;
  out.x = x;
  out.y = p.y + local.y;
  out.z = z;
  return out;
}

/** Rotates a direction (no translation). */
export function transformDirection(p: Pose, local: Readonly<Vec3>, out: Vec3 = { x: 0, y: 0, z: 0 }): Vec3 {
  const [c, s] = cosSin(p.yaw);
  const x = local.x * c + local.z * s;
  const z = -local.x * s + local.z * c;
  out.x = x;
  out.y = local.y;
  out.z = z;
  return out;
}

/** parent ∘ child: the child pose expressed in the parent's parent space. */
export function composePose(parent: Pose, child: Pose): Pose {
  const p = transformPoint(parent, child);
  return { x: p.x, y: p.y, z: p.z, yaw: wrapAngle(parent.yaw + child.yaw) };
}

export function invertPose(p: Pose): Pose {
  const yaw = -p.yaw;
  const [c, s] = cosSin(yaw);
  return {
    x: -(p.x * c + p.z * s),
    y: -p.y,
    z: -(-p.x * s + p.z * c),
    yaw: wrapAngle(yaw),
  };
}

export function forwardOf(yaw: number): Vec3 {
  const [c, s] = cosSin(yaw);
  return { x: s, y: 0, z: c };
}

export function rightOf(yaw: number): Vec3 {
  const [c, s] = cosSin(yaw);
  return { x: c, y: 0, z: -s };
}

export function posesEqual(a: Pose, b: Pose, epsilon = 1e-6): boolean {
  return (
    Math.abs(a.x - b.x) < epsilon &&
    Math.abs(a.y - b.y) < epsilon &&
    Math.abs(a.z - b.z) < epsilon &&
    Math.abs(wrapAngle(a.yaw - b.yaw)) < epsilon
  );
}
